import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useContent } from "@/hooks/use-content";
import { automationActive, usePlans, usePublishPolicy } from "./agentic";
import { EscalationBanner } from "@/components/escalation-banner";
import { BrandTab } from "./brand-tab";
import { CalendarTab } from "./calendar-tab";
import { IdeasTab } from "./ideas-tab";
import { InboxTab, reviewList, reviewOrder } from "./inbox-tab";
import { PlanTab } from "./plan-tab";
import { PublishedTab } from "./published-tab";
import { QualityTab } from "./quality-tab";
import { ReadyTab } from "./ready-tab";
import { ReviewView } from "./review-view";
import type { StudioItem } from "./helpers";
import { useActiveWorkspace, useLastVisit } from "./use-studio";

export type SimpleTabId = "review" | "calendar" | "plan" | "ready" | "published" | "quality" | "ideas" | "brand";

const TABS: { id: SimpleTabId; label: string }[] = [
  { id: "review", label: "Needs your review" },
  { id: "calendar", label: "Calendar" },
  { id: "plan", label: "Plan" },
  { id: "ready", label: "Ready to publish" },
  { id: "published", label: "Published" },
  { id: "quality", label: "Quality" },
  { id: "ideas", label: "Ideas & requests" },
  { id: "brand", label: "Photos & brand" },
];

/**
 * Old `?tab=` values (from the dashboard, the approval nudge, the Ad Center, Peggy's deep links)
 * land on the closest new tab. Unknown values fall back to "Needs your review", never to Create.
 */
export function resolveSimpleTab(tabParam: string | null, approvalStatus?: string | null): SimpleTabId {
  const t = (tabParam || "").toLowerCase();
  if ((TABS as { id: string }[]).some((x) => x.id === t)) return t as SimpleTabId;
  switch (t) {
    case "library":
      if (approvalStatus === "pending_approval" || approvalStatus === "pending") return "review";
      if (approvalStatus === "approved") return "ready";
      return "published";
    case "queue":
    case "create":
    case "repurpose":
    case "intelligence":
    case "seo":
    case "personal-brand":
    case "pages":
    case "videos":
      return "ideas";
    default:
      // blogs, playbook, unknown and missing values
      return "review";
  }
}

/** Old tabs that only exist in the legacy studio (Page Builder, Video Studio). */
export function isLegacyOnlyTab(tabParam: string | null): boolean {
  const t = (tabParam || "").toLowerCase();
  return t === "pages" || t === "videos";
}

/** A deep link that pointed at a legacy-only tool. It lands on "Ideas & requests", which offers the tool from there. */
export interface AdvancedToolLink {
  tool: "pages" | "videos";
  /** The same link opened in the advanced (legacy) studio, with every original parameter kept. */
  href: string;
  /** For Video Studio links: the script the Ad Center handed over, so Draper can be asked to make the video instead. */
  script: string | null;
}

export function readAdvancedTool(search: string): AdvancedToolLink | null {
  const p = new URLSearchParams(search);
  const raw = (p.get("tool") || p.get("tab") || "").toLowerCase();
  if (!isLegacyOnlyTab(raw)) return null;
  p.delete("tool");
  p.set("tab", raw);
  p.set("legacy", "1");
  return { tool: raw as "pages" | "videos", href: `/content?${p.toString()}`, script: p.get("script") };
}

function readUrl(): { tab: SimpleTabId; itemId: number | null } {
  const p = new URLSearchParams(window.location.search);
  const raw = p.get("item") ?? p.get("itemId");
  const itemId = raw && /^[0-9]+$/.test(raw) ? Number(raw) : null;
  const tabParam = p.get("tool") && isLegacyOnlyTab(p.get("tool")) ? p.get("tool") : p.get("tab");
  return { tab: resolveSimpleTab(tabParam, p.get("approvalStatus")), itemId };
}

function writeUrl(tab: SimpleTabId, itemId: number | null, mode: "push" | "replace") {
  const p = new URLSearchParams(window.location.search);
  p.set("tab", tab);
  if (itemId !== null) p.set("item", String(itemId));
  else p.delete("item");
  p.delete("itemId");
  p.delete("approvalStatus");
  // Tool hand-over parameters were captured when the studio opened; keep the address bar clean afterwards.
  for (const k of ["tool", "editPage", "script", "brief"]) p.delete(k);
  const url = `${window.location.pathname}?${p.toString()}`;
  if (mode === "push") window.history.pushState({ studioItem: itemId }, "", url);
  else window.history.replaceState({}, "", url);
}

export function SimpleStudio({ renderEditor }: { renderEditor: (item: StudioItem, onClose: () => void) => ReactNode }) {
  const { workspaceId, workspace, engine } = useActiveWorkspace();
  const { data, isLoading, isError, refetch } = useContent();
  const items = useMemo(() => (data || []) as StudioItem[], [data]);
  const lastVisit = useLastVisit(workspaceId);
  const { policy, available: policyKnown } = usePublishPolicy();
  const { plans } = usePlans();
  const plansWaiting = useMemo(() => plans.filter((p) => p.status === "draft" || p.status === "reviewed").length, [plans]);

  const [{ tab, itemId }, setNav] = useState(readUrl);
  const [advancedTool, setAdvancedTool] = useState<AdvancedToolLink | null>(() => readAdvancedTool(window.location.search));

  // Keep the screen in step with the browser's back/forward buttons.
  useEffect(() => {
    const onPop = () => setNav(readUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Normalise the address bar once (old ?tab= values become the new ones; ?itemId= becomes ?item=).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("tab") !== tab || p.has("itemId") || p.has("approvalStatus") || p.has("tool") || p.has("editPage") || p.has("script")) {
      writeUrl(tab, itemId, "replace");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTab = useCallback((next: SimpleTabId) => {
    writeUrl(next, null, "replace");
    setNav({ tab: next, itemId: null });
  }, []);

  const openItem = useCallback(
    (id: number) => {
      writeUrl(tab, id, "push");
      setNav({ tab, itemId: id });
    },
    [tab],
  );

  const closeItem = useCallback(() => {
    if (window.history.state && "studioItem" in window.history.state && window.history.state.studioItem) {
      window.history.back(); // popstate updates the screen
      return;
    }
    writeUrl(tab, null, "replace");
    setNav({ tab, itemId: null });
  }, [tab]);

  // Only what needs a person: manual-policy items, NEEDS_FIX, held, and off-plan items whose pre-publish check failed.
  const reviewEntries = useMemo(() => reviewList(items, policy, workspace), [items, policy, workspace]);
  const ordered = useMemo(() => reviewOrder(reviewEntries), [reviewEntries]);
  const automatic = automationActive(policy, workspace);

  const next = useMemo(() => {
    if (itemId === null || ordered.length === 0) return null;
    const idx = ordered.findIndex((i) => i.id === itemId);
    if (idx >= 0) {
      const n = ordered[idx + 1];
      return n ? { id: n.id, position: idx + 2, total: ordered.length } : null;
    }
    return { id: ordered[0].id, position: 1, total: ordered.length };
  }, [ordered, itemId]);

  const tabLabel = TABS.find((t) => t.id === tab)?.label ?? "the list";
  const open = (item: StudioItem) => openItem(item.id);

  if (!workspaceId) return null;

  return (
    <div className="mx-auto w-full min-w-0 max-w-5xl space-y-5 px-1 sm:px-0" data-testid="simple-studio" data-engine={engine}>
      {/* An open P0 stays pinned here, in every tab and while reading an item, until it is acknowledged. */}
      <EscalationBanner sticky />

      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Content Studio</h1>
        <p className="mt-0.5 text-sm text-muted-foreground" data-testid="studio-subtitle">
          {automatic
            ? "Your team works to a plan you approve. Pieces on a Manual channel wait for you; the others publish by themselves unless you hold them."
            : "Read what your team made, approve it, and decide when it goes out. Nothing is published without you."}
        </p>
      </div>

      {itemId === null && (
        <nav className="grid grid-cols-2 gap-1 rounded-xl border border-border bg-muted/30 p-1 sm:flex sm:flex-wrap sm:gap-1.5" aria-label="Content Studio sections" data-testid="simple-tabs">
          {TABS.map((t) => {
            const count = t.id === "review" && reviewEntries.length > 0 ? reviewEntries.length : t.id === "plan" && plansWaiting > 0 ? plansWaiting : null;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => goTab(t.id)}
                aria-current={active ? "page" : undefined}
                data-testid={`tab-${t.id}`}
                className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors sm:justify-start ${
                  t.id === "review" ? "col-span-2 sm:col-span-1" : ""
                } ${
                  active ? "bg-blue-600 text-white shadow" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
              >
                {t.label}
                {count !== null && (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold ${active ? "bg-white/25 text-white" : "bg-amber-500/20 text-amber-300"}`}
                    data-testid={`tab-count-${t.id}`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      )}

      {isError && !data ? (
        <div className="space-y-3 py-16 text-center" data-testid="studio-error">
          <p className="text-sm text-foreground">Couldn't load your content.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : itemId !== null ? (
        isLoading && !data ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="mr-2 animate-spin" size={18} /> Opening…
          </div>
        ) : (
          <ReviewView
            itemId={itemId}
            listItem={items.find((i) => i.id === itemId)}
            workspace={workspace}
            onBack={closeItem}
            backLabel={`Back to ${tabLabel}`}
            next={next}
            onOpenItem={openItem}
            renderEditor={renderEditor}
          />
        )
      ) : (
        <>
          {tab === "review" && (
            <InboxTab
              entries={reviewEntries}
              loading={isLoading}
              onOpen={open}
              lastVisit={lastVisit}
              policy={policy}
              workspace={workspace}
              policyKnown={policyKnown}
              onGoCalendar={() => goTab("calendar")}
            />
          )}
          {tab === "calendar" && <CalendarTab items={items} onOpenItem={openItem} onGoPlan={() => goTab("plan")} />}
          {tab === "plan" && <PlanTab workspace={workspace} onOpenItem={openItem} />}
          {tab === "ready" && <ReadyTab items={items} loading={isLoading} workspace={workspace} onOpen={open} onGoCalendar={() => goTab("calendar")} />}
          {tab === "published" && <PublishedTab items={items} loading={isLoading} onOpen={open} />}
          {tab === "quality" && <QualityTab />}
          {tab === "ideas" && (
            <IdeasTab workspaceId={workspaceId} engine={engine} advancedTool={advancedTool} onDismissAdvancedTool={() => setAdvancedTool(null)} />
          )}
          {tab === "brand" && <BrandTab workspaceId={workspaceId} workspace={workspace} />}
        </>
      )}

      {itemId === null && (
        <p className="border-t border-border pt-4 text-xs text-muted-foreground">
          <a href="/content?legacy=1" className="underline underline-offset-2 hover:text-foreground" data-testid="link-advanced-legacy">
            Advanced (legacy tools)
          </a>
        </p>
      )}
    </div>
  );
}
